import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
  useRef,
} from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import { api, getListAPI, getUserId } from '../../services';
import { STATUS_ATIVO, formatDateDDMMYYYY, SECONDARY_COLOR } from '../../utils';

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: SECONDARY_COLOR,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    cursor: 'pointer',
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}))(TableRow);

const styles = {
  root: {
    margin: 0,
    padding: '16px',
  },
  closeButton: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    color: 'black',
  },
};

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography style={styles.root} {...other}>
      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          style={styles.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const NOMES_ANDAR = ['Térreo', '1º Aéreo', '2º Aéreo'];

function getNomeAndar(andar) {
  return NOMES_ANDAR[andar - 1] || `Andar ${andar}`;
}

const DialogoCriarSaida = forwardRef(({ handleClose = () => {} }, ref) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const [produto, setProduto] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [valueDebounce, setValueDebounce] = useState('');
  const [options, setOptions] = useState([]);

  const [combos, setCombos] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const refDialogoConfirmacao = useRef(null);

  const handleCloseDialog = () => {
    setOpen(false);
    setProduto(null);
    setInputValue('');
    setCombos([]);
  };

  useImperativeHandle(ref, () => ({
    handleOpen() {
      setOpen(true);
    },
  }));

  useEffect(() => {
    const timer = setTimeout(() => setValueDebounce(inputValue), 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    let active = true;

    (async () => {
      const { data } = await getListAPI(
        'produtos_search_filter',
        ['id', 'asc'],
        [1, 10],
        {
          nome: valueDebounce,
          status: STATUS_ATIVO,
        },
        [],
      );
      if (active) setOptions(data);
    })();

    return () => {
      active = false;
    };
  }, [valueDebounce]);

  async function getCombos(produtoLocal) {
    setCarregando(true);
    const response = await api.get(
      `/enderecos_exposicao_andares_produtos/combos/${produtoLocal.id}`,
    );
    setCombos(response.data);
    setCarregando(false);
  }

  function handleSelecionarProduto(newValue) {
    setProduto(newValue);
    setCombos([]);
    if (newValue) getCombos(newValue);
  }

  function handleSelecionarCombo(row) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'REGISTRAR SAÍDA',
        `Confirma a saída de "${produto.nome}" do endereço ${
          row.codigo
        }, ${getNomeAndar(row.andar)}?`,
        row,
      );
    }
  }

  async function handleConfirma(row) {
    try {
      await api.put(
        `/enderecos_exposicao_andares_produtos/status/inativar/${row.andar_produto_id}`,
        { user_id: getUserId() },
      );
      enqueueSnackbar('Saída registrada com sucesso!', { variant: 'success' });
      handleCloseDialog();
      handleClose();
    } catch (erros) {
      enqueueSnackbar(`${erros}`, { variant: 'error' });
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title" onClose={handleCloseDialog}>
          Registrar Saída
        </DialogTitle>
        <MuiDialogContent>
          <Autocomplete
            value={produto}
            onChange={(event, newValue) => handleSelecionarProduto(newValue)}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) =>
              setInputValue(newInputValue)
            }
            getOptionSelected={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.nome || ''}
            options={options}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Produto"
                autoFocus
                fullWidth
              />
            )}
          />

          {produto && (
            <>
              {carregando ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '16px',
                  }}
                >
                  <CircularProgress size={24} />
                </div>
              ) : combos.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Endereço</StyledTableCell>
                        <StyledTableCell>Andar</StyledTableCell>
                        <StyledTableCell>Validade mais próxima</StyledTableCell>
                        <StyledTableCell>Permanência</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {combos.map((row) => (
                        <StyledTableRow
                          key={row.andar_produto_id}
                          onClick={() => handleSelecionarCombo(row)}
                        >
                          <StyledTableCell>{row.codigo}</StyledTableCell>
                          <StyledTableCell>
                            {getNomeAndar(row.andar)}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.proxima_validade
                              ? formatDateDDMMYYYY(row.proxima_validade)
                              : 'Sem validade'}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.dias_permanencia !== null
                              ? `${row.dias_permanencia} dia${
                                  row.dias_permanencia === 1 ? '' : 's'
                                }`
                              : '-'}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ padding: '16px' }}
                >
                  Este produto não está presente em nenhum endereço.
                </Typography>
              )}
            </>
          )}
        </MuiDialogContent>
      </Dialog>
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
    </>
  );
});

export default DialogoCriarSaida;
